export const NOTIFICATION_PAGE_SIZE = 20;

function pickArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.notifications)) return payload.notifications;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.list)) return payload.list;
  const nested = payload.data;
  if (nested && typeof nested === "object") {
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.notifications)) return nested.notifications;
    if (Array.isArray(nested.rows)) return nested.rows;
    if (Array.isArray(nested.list)) return nested.list;
  }
  return [];
}

function pickNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function pickBool(value) {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return null;
}

/**
 * Normalize GET notification/get_notification for both legacy arrays
 * and paginated { data, total, unread_count, page, has_more } bodies.
 */
export function parseNotificationListResponse(
  apiBody,
  { page = 1, limit = NOTIFICATION_PAGE_SIZE } = {},
) {
  const items = pickArray(apiBody);
  const root =
    apiBody && typeof apiBody === "object" && !Array.isArray(apiBody)
      ? apiBody
      : {};
  const nested =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? root.data
      : {};

  const total = pickNumber(
    nested.total,
    nested.total_count,
    nested.totalCount,
    root.total,
    root.total_count,
    root.totalCount,
    root.count,
  );
  const unreadCount = pickNumber(
    nested.unread_count,
    nested.unreadCount,
    nested.unread,
    root.unread_count,
    root.unreadCount,
    root.unread,
  );
  const resPage =
    pickNumber(nested.page, nested.current_page, root.page, root.current_page) ??
    page;
  const resLimit =
    pickNumber(
      nested.limit,
      nested.page_size,
      nested.pageSize,
      root.limit,
      root.page_size,
      root.pageSize,
    ) ?? limit;

  const explicitHasMore = pickBool(
    nested.has_more ?? nested.hasMore ?? root.has_more ?? root.hasMore,
  );

  let hasMore;
  if (explicitHasMore != null) {
    hasMore = explicitHasMore;
  } else if (total != null) {
    hasMore = resPage * resLimit < total;
  } else if (items.length > resLimit) {
    // Legacy unpaginated dump — do not keep requesting.
    hasMore = false;
  } else {
    hasMore = items.length === resLimit;
  }

  return {
    items,
    page: resPage,
    limit: resLimit,
    total,
    unreadCount,
    hasMore,
  };
}

export function mergeNotificationPages(existing = [], incoming = []) {
  const seen = new Set();
  const out = [];
  [...existing, ...incoming].forEach((item) => {
    const id = item?.notification_id ?? item?.notificationId ?? item?.id;
    const key = id != null && id !== "" ? String(id) : "";
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    out.push(item);
  });
  return out;
}

export function markNotificationViewedInList(list, notificationId) {
  return (Array.isArray(list) ? list : []).map((item) =>
    String(item?.notification_id ?? item?.notificationId) ===
    String(notificationId)
      ? { ...item, view_status: "Viewed" }
      : item,
  );
}

export const emptyNotificationMeta = {
  page: 0,
  hasMore: true,
  unreadCount: null,
  total: null,
  loading: false,
  loadingMore: false,
};
